<script lang="ts">
	import { untrack } from 'svelte'
	import { t } from 'svelte-i18n'
	import type {
		OutputObject,
		Modality
	} from '@semanticedge/persistence-service-noheaders-client/src'
	import {
		Button,
		TabCase,
		ButtonSize,
		ButtonType,
		DetailedSelect,
		OutputObjectForm,
		OutputObjectPromptVariantsForm
	} from '@repo/commons/components'
	import {
		ProjectViewDialog,
		type DetailedSelectItem,
		type FormValidationItem,
		type OutputObjectPromptVariantsRequest,
		type OutputObjectPromptVariantsResponse,
		type ProjectViewModel,
		type TabCaseItem,
		type TableData,
		type ValidatorFunctionFactory
	} from '@repo/commons/models'
	import {
		getAllOutputObjectsFromProject,
		validateEmptyString,
		validateForm
	} from '@repo/commons/utils'
	import { MODALITY_LABELS } from './constants.js'
	import type { OutputObjectSubmitHandler } from './types.js'

	type Props = {
		onclose: VoidFunction
		project: ProjectViewModel | null
		onSubmit: OutputObjectSubmitHandler
		editingOutputObject?: TableData | null
		selectedModality: Modality
		existingOutputObjectIds: readonly string[]
		nameReadOnly?: boolean
		createNameValidator?: (initialName?: string) => ValidatorFunctionFactory
		promptVariantsRequest: OutputObjectPromptVariantsRequest | null
	}

	let {
		project,
		onclose,
		onSubmit,
		editingOutputObject,
		existingOutputObjectIds,
		nameReadOnly = false,
		createNameValidator,
		selectedModality = $bindable(),
		promptVariantsRequest = $bindable()
	}: Props = $props()

	let outputObject = $state<Partial<OutputObject> | undefined>(
		untrack(() => editingOutputObject ?? undefined)
	)
	let isSaving = $state(false)
	let isOutputObjectTouched = $state(false)
	let outputObjectFormComponent = $state<{
		validate: (isSubmitting?: boolean) => Record<string, string>
	} | null>(null)

	const isEditMode = $derived(!!editingOutputObject)
	const isPromptVariantsOpen = $derived(!!promptVariantsRequest)
	const supportedModalities = $derived(project?.metadata?.supportedModalities ?? [])
	const allOutputObjects = $derived(project ? getAllOutputObjectsFromProject(project) : [])

	const outputObjectOptions: DetailedSelectItem[] = $derived(
		allOutputObjects.map(({ id = '', name }) => {
			const isAlreadyAdded = !isEditMode && existingOutputObjectIds.includes(id)

			return {
				id,
				disabled: isAlreadyAdded,
				title: name || $t('OutputObjectModal.unnamed'),
				description: isAlreadyAdded ? $t('OutputObjectModal.alreadyExists') : undefined
			}
		})
	)

	const selectedOutputObjectOption = $derived(
		outputObjectOptions.find(({ id }) => id === outputObject?.id) ?? null
	)

	const validationSchema: Record<'outputObjectId', FormValidationItem> = $derived({
		outputObjectId: { formComponent: outputObject?.id ?? '', validators: [validateEmptyString] }
	})

	const errors = $derived(
		validateForm(validationSchema, { outputObjectId: isOutputObjectTouched }, false)
	)

	const modalityTabs: TabCaseItem[] = $derived(
		supportedModalities.map((modality) => ({
			id: modality,
			isActive: modality === selectedModality,
			name: MODALITY_LABELS[modality] ?? String(modality)
		}))
	)

	const handleSelectOutputObject = (item: DetailedSelectItem | null): void => {
		isOutputObjectTouched = true

		if (!item) {
			outputObject = undefined

			return
		}

		const selectedOutputObject = allOutputObjects.find(({ id }) => id === item.id)

		if (selectedOutputObject) outputObject = { ...selectedOutputObject }
	}

	const handleModalitySelect = (tabId: string): void => {
		const modality = supportedModalities.find((supportedModality) => supportedModality === tabId)

		if (modality) selectedModality = modality
	}

	const submitOutputObject = async (): Promise<void> => {
		if (isSaving) return

		isOutputObjectTouched = true

		if (!outputObject) return
		const outputObjectErrors = outputObjectFormComponent?.validate(true) ?? {}
		if (outputObjectErrors.name) return

		isSaving = true

		try {
			const isSaved = await onSubmit(outputObject, isEditMode)

			if (isSaved) onclose()
		} catch {
			// Don't close on error; parent handles toast
		} finally {
			isSaving = false
		}
	}

	const handleFormSubmit = (event: SubmitEvent): void => {
		event.preventDefault()
		submitOutputObject()
	}

	const handlePromptVariantsRequest = (request: OutputObjectPromptVariantsRequest): void => {
		if (request.dialogType !== ProjectViewDialog.OUTPUT_OBJECT) return

		promptVariantsRequest = request
	}

	const handlePromptVariantsResponse = ({ prompts }: OutputObjectPromptVariantsResponse): void => {
		if (outputObject) outputObject.prompts = prompts

		promptVariantsRequest = null
	}

	const closePromptVariants = (): void => {
		promptVariantsRequest = null
	}
</script>

<div class="modal-content" class:hidden={isPromptVariantsOpen} aria-hidden={isPromptVariantsOpen}>
	{#if modalityTabs.length > 0}
		<div class="modality-tabs-wrapper">
			<TabCase tabs={modalityTabs} onpress={handleModalitySelect} />
		</div>
	{/if}
	<form onsubmit={handleFormSubmit}>
		<div class="form-group">
			<DetailedSelect
				disabled={isEditMode}
				items={outputObjectOptions}
				error={errors.outputObjectId}
				onSelect={handleSelectOutputObject}
				label={$t('OutputObjectModal.select')}
				selectedItem={selectedOutputObjectOption}
			/>
			{#if outputObject && project}
				<OutputObjectForm
					bind:this={outputObjectFormComponent}
					{project}
					bind:outputObject
					{selectedModality}
					showToastOnErrors={false}
					{nameReadOnly}
					{createNameValidator}
					promptVariantsEditMode="inline"
					onPromptVariantsRequest={handlePromptVariantsRequest}
					visibility={{ addNewPromptButton: false }}
				/>
			{/if}
		</div>
	</form>
	<div class="modal-footer">
		<Button
			onclick={submitOutputObject}
			disabled={isSaving}
			buttonType={ButtonType.BRAND}
			buttonSize={ButtonSize.LARGE}
			buttonText={isEditMode ? $t('OutputObjectModal.save') : $t('OutputObjectModal.add')}
		/>
		<Button
			onclick={onclose}
			buttonText={$t('Button.Cancel')}
			buttonSize={ButtonSize.LARGE}
			buttonType={ButtonType.TERTIARY_INVERSE}
		/>
	</div>
</div>

<div class="modal-content" class:hidden={!isPromptVariantsOpen} aria-hidden={!isPromptVariantsOpen}>
	{#if promptVariantsRequest && project}
		<OutputObjectPromptVariantsForm
			{project}
			{promptVariantsRequest}
			onClose={closePromptVariants}
			onPromptVariantsResponse={handlePromptVariantsResponse}
		/>
	{/if}
</div>

<style lang="postcss">
	.modal-content {
		@apply flex flex-col gap-4 w-[434px] max-w-full;
	}
	.modal-content.hidden {
		display: none;
	}
	.modality-tabs-wrapper {
		@apply mb-2;
	}
	.form-group {
		@apply flex flex-col gap-6;
	}
	.modal-footer {
		@apply flex gap-4 mt-4;
	}
</style>
